import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAllTeamTimeEntries } from '@/hooks/use-time-entries';
import { useMemo } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { extractErrorMessage } from '@/lib/errors';
import { groupTeamTime } from '../lib/team-time-groups';

interface TeamTimeTabProps {
  from: string;
  to: string;
  isProjectView?: boolean;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0 && m === 0) return '0m';
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function TeamTimeTab({ from, to, isProjectView }: TeamTimeTabProps) {
  // `from`/`to` are `weekWindow`'s exact UTC-digit day edges (time-page.tsx).
  // Until the backfill converts legacy rows, a legacy night row is matched
  // here by its real instant against those whole UTC-digit days rather than
  // by the viewer's day the Weekly Timesheet groups it under, so it can count
  // in the neighbouring week here; the runbook runs the backfill right after
  // this frontend deploys, in the same window, which closes the gap. [HW-916]
  //
  // Every page of the window: the cards' totals are summed from these rows, so
  // each total and each card holds every entry the reader can see. [HW-930]
  const { data, isLoading, error } = useAllTeamTimeEntries({ from, to });
  const errorMessage = error
    ? extractErrorMessage(error, 'Could not load time entries for this period.')
    : null;
  const formatName = useEmployeeNameFormatter();

  const grouped = useMemo(
    () => groupTeamTime(data?.data ?? [], isProjectView ? 'project' : 'employee', formatName),
    [data, isProjectView, formatName],
  );

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>;
  }

  // Checked before the empty state: a failed read with no rows to show must
  // not say "No time entries", which claims nobody logged time. A failed
  // refetch keeps the rows already loaded, shown under the notice below.
  // [HW-930]
  if (errorMessage && grouped.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p role="alert" className="text-sm text-destructive">
          {errorMessage}
        </p>
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-muted-foreground">No time entries for this period</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <p
          role="alert"
          className="rounded-md bg-destructive/5 px-3 py-2 text-xs leading-snug text-destructive"
        >
          {errorMessage}
          <span className="block text-muted-foreground">The figures below may be out of date.</span>
        </p>
      )}
      {grouped.map(([id, group]) => (
        <Card key={id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isProjectView ? (
                  <>
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: group.color }} />
                    <span>{group.name}</span>
                  </>
                ) : (
                  <>
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={group.avatarUrl || undefined} />
                      <AvatarFallback className="text-[8px]">
                        {group.name.split(' ').map((n) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <span>{group.name}</span>
                  </>
                )}
              </div>
              <span className="font-mono text-muted-foreground">{formatDuration(group.totalSeconds)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {group.entries.slice(0, 5).map((entry) => (
                <div key={entry.id} className="flex items-center justify-between py-1 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    {entry.task && (
                      <span className="font-mono text-primary shrink-0">{entry.task.key}</span>
                    )}
                    <span className="text-muted-foreground truncate">
                      {entry.description || entry.task?.title || '—'}
                    </span>
                  </div>
                  <span className="font-mono text-muted-foreground shrink-0 ml-2">
                    {formatDuration(entry.duration)}
                  </span>
                </div>
              ))}
              {group.entries.length > 5 && (
                <p className="text-[11px] text-muted-foreground pt-1">
                  +{group.entries.length - 5} more entries
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
